import React, { useState } from 'react';
import { Button, Icon, Tag } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './CodeBlock.css';

interface CodeBlockProps {
  language: string;
  children: string;
  isDark: boolean;
  filename?: string;
}

/**
 * Enhanced code block component with copy functionality, line numbers,
 * and language badges for better readability of AI-generated code.
 */
const CodeBlock: React.FC<CodeBlockProps> = ({ language, children, isDark, filename }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  // Map common language aliases to display names
  const getLanguageDisplay = (lang: string): string => {
    const languageMap: Record<string, string> = {
      js: 'JavaScript',
      jsx: 'React JSX',
      ts: 'TypeScript',
      tsx: 'React TSX',
      py: 'Python',
      python: 'Python',
      java: 'Java',
      cpp: 'C++',
      c: 'C',
      cs: 'C#',
      go: 'Go',
      rust: 'Rust',
      ruby: 'Ruby',
      php: 'PHP',
      swift: 'Swift',
      kotlin: 'Kotlin',
      sql: 'SQL',
      bash: 'Bash',
      sh: 'Shell',
      json: 'JSON',
      yaml: 'YAML',
      yml: 'YAML',
      xml: 'XML',
      html: 'HTML',
      css: 'CSS',
      scss: 'SCSS',
      markdown: 'Markdown',
      md: 'Markdown',
    };

    return languageMap[lang.toLowerCase()] || lang.toUpperCase();
  };

  return (
    <div className={`code-block-container ${isDark ? 'dark' : 'light'}`}>
      {/* Code Block Header */}
      <div className="code-block-header">
        <div className="code-block-info">
          {filename && (
            <span className="code-block-filename">
              <Icon icon={IconNames.DOCUMENT} size={12} />
              {filename}
            </span>
          )}
          <Tag minimal className="code-block-language">
            {getLanguageDisplay(language)}
          </Tag>
        </div>
        <Button
          minimal
          small
          icon={copied ? IconNames.TICK : IconNames.DUPLICATE}
          text={copied ? 'Copied!' : 'Copy'}
          onClick={handleCopy}
          className="code-block-copy-btn"
          intent={copied ? 'success' : 'none'}
        />
      </div>

      {/* Syntax Highlighted Code */}
      <div className="code-block-body">
        <SyntaxHighlighter
          language={language}
          style={isDark ? vscDarkPlus : vs}
          showLineNumbers={true}
          wrapLines={false}
          lineNumberStyle={{
            minWidth: '2.5em',
            paddingRight: '1em',
            color: isDark ? '#6e7681' : '#8b949e',
            marginRight: '0.5em',
            userSelect: 'none',
            opacity: 0.5,
          }}
          customStyle={{
            margin: 0,
            borderRadius: 0,
            fontSize: '13px',
            lineHeight: '1.6',
            padding: '12px 16px',
            background: 'transparent',
          }}
          codeTagProps={{
            style: {
              fontFamily: "'JetBrains Mono', 'Fira Code', 'Monaco', 'Courier New', monospace",
              fontSize: '13px',
              background: 'transparent',
            },
          }}
          lineProps={() => ({
            style: { 
              background: 'transparent',
              display: 'block',
              border: 'none',
              borderTop: 'none',
              borderBottom: 'none',
              borderLeft: 'none',
              borderRight: 'none',
              outline: 'none',
              boxShadow: 'none',
            }
          })}
        >
          {children}
        </SyntaxHighlighter>
      </div>
    </div>
  );
};

export default CodeBlock;

